import { useLocale } from '../i18n/LocaleProvider'
import {
  ChartPie, Globe, Translate, ShareNetwork,
  ChartBar, Headset, Desktop, ChartLineUp,
  PaperPlaneTilt, Handshake, ChatText, Coins,
  Buildings, Stamp, FilePlus, Receipt, Bank,
} from '@phosphor-icons/react'
import type { Icon } from '@phosphor-icons/react'
import { useInquiry } from './InquirySheet'
import { SiteButton } from './SiteButton'
import './ServicesSection.css'

const plans: {
  name: string; price: string; period: string; featured: boolean; badge?: string;
  items: { icon: Icon; text: string }[]; cta: string; note: string;
}[] = [
  {
    name: 'Retainer Plan',
    price: '$2,480',
    period: '/month',
    featured: false,
    items: [
      { icon: ChartBar,    text: 'Social media management (posting & analytics)' },
      { icon: Headset,     text: 'Japanese-language customer success' },
      { icon: Desktop,     text: 'Japanese-language client interface' },
      { icon: ChartLineUp, text: 'Bi-monthly KPI reports' },
    ],
    cta: 'Book a Discovery Call',
    note: 'Available exclusively to Starter Pack graduates and active clients',
  },
  {
    name: 'Starter Pack',
    price: '$11,680',
    period: 'one-time',
    featured: true,
    badge: 'Start Here',
    items: [
      { icon: ChartPie,     text: 'Japan market research & analysis' },
      { icon: Globe,        text: 'Japan-localized landing page' },
      { icon: Translate,    text: 'Business document translation' },
      { icon: ShareNetwork, text: 'Social media setup & management (2 months)' },
      { icon: Buildings,    text: 'Japanese entity setup' },
      { icon: Stamp,        text: 'Corporate seal set' },
      { icon: FilePlus,     text: 'Document generation tool access' },
      { icon: Receipt,      text: 'Tax authority filing coordination' },
      { icon: Bank,         text: 'Bank account opening support' },
    ],
    cta: 'Get Started',
    note: '6–8 week delivery · Early client: 50% off ($5,840)',
  },
  {
    name: 'Performance Sales',
    price: '$399',
    period: '/qualified meeting',
    featured: false,
    items: [
      { icon: PaperPlaneTilt, text: 'Outreach to Japanese enterprise decision-makers' },
      { icon: Handshake,      text: 'Charged only upon confirmed meeting' },
      { icon: ChatText,       text: 'Native Japanese sales messaging' },
      { icon: Coins,          text: 'Zero upfront cost' },
    ],
    cta: 'Learn More',
    note: 'Reserved for current Starter Pack and Retainer Plan clients',
  },
]

const alacarte = [
  { icon: ChartPie,     name: 'Market Research & Analysis',               price: '~$4,000' },
  { icon: Globe,        name: 'Japan-Localized Landing Page',             price: '~$5,000' },
  { icon: Translate,    name: 'Business Document Translation',            price: '~$1,500' },
  { icon: ShareNetwork, name: 'Social Media Setup & Management (2 mo.)', price: '~$1,500' },
]

export default function ServicesSection() {
  const { t } = useLocale()
  const { openInquiry } = useInquiry()
  return (
    <section className="services-section" id="services" style={{ background: 'var(--home-bg)' }}>
      <div className="container">
        <div className="section-header">
          <span className="section-jp">{t("Services & Pricing")}</span>
          <h2>{t("Choose Your Entry Path")}</h2>
          <p>{t("All prices in USD. USD 1 = JPY 150 (March 2026). English service agreement. Payment via Stripe or Wise.")}</p>
        </div>

        <div className="plans-grid">
          {plans.map((p) => (
            <div className={`plan-card${p.featured ? ' plan-card--featured' : ''}`} key={p.name}>
              {p.featured && <div className="plan-badge">{t(p.badge ?? 'Most Popular')}</div>}
              <div className="plan-name">{t(p.name)}</div>
              <div className="plan-price-row">
                <span className="plan-price">{p.price}</span>
                <span className="plan-period">{t(p.period)}</span>
              </div>
              <ul className="plan-items">
                {p.items.map(({ icon: Icon, text }) => (
                  <li key={text}>
                    <Icon size={14} weight="bold" />
                    {t(text)}
                  </li>
                ))}
              </ul>
              <SiteButton
                variant={p.featured ? 'accent' : 'light'}
                size="plan"
                fullWidth
                onClick={() => openInquiry({ message: t("I'm interested in the {plan}.", { plan: t(p.name) }) })}
              >
                {t(p.cta)}
              </SiteButton>
              <p className="plan-note">{t(p.note)}</p>
            </div>
          ))}
        </div>

        {/* À la carte */}
        <div className="alacarte">
          <h3>{t("Or Pick Individual Services")}</h3>
          <div className="alacarte-grid">
            {alacarte.map(a => (
              <div className="alacarte-row" key={a.name}>
                <span className="alacarte-label">
                  <a.icon size={15} weight="bold" />
                  {t(a.name)}
                </span>
                <span className="alacarte-price">{a.price}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
